import { Folder } from '@/api/generatedApi';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { LIST_FOLDER_CONTENTS_QUERY_KEY } from './useFetchFolderContents';
import { GET_FOLDER_HIERARCHY_QUERY_KEY } from './useFolderHierarchy';
import { ASSET_STATE_COUNTS_QUERY_KEY } from '../assets/useGetAssetStateCounts';
import { HAS_SUBFOLDER_DESCRIPTIONS_QUERY_KEY } from './useHasSubfolderDescriptions';
import { FOLDER_COLUMN_VISIBILITY_QUERY_KEY } from './useFolderColumnVisibility';

export const useDeleteFolder = ({
  folder,
  onSuccess,
  onError,
}: {
  folder?: Folder;
  onSuccess?: () => void;
  onError?: (error: string) => void;
} = {}) => {
  const queryClient = useQueryClient();
  const { getTenantId } = useTenants();
  const tenantId = getTenantId();
  const { mutate, isPending, error } = useMutation({
    mutationKey: ['deleteFolder', folder?.id],
    mutationFn: () => api.folders.deleteFolder(tenantId, folder!.id),
    onSuccess: () => {
      onSuccess?.();
      queryClient.resetQueries({
        queryKey: [LIST_FOLDER_CONTENTS_QUERY_KEY],
        exact: false,
      });
      queryClient.resetQueries({ queryKey: [GET_FOLDER_HIERARCHY_QUERY_KEY], exact: false });
      queryClient.resetQueries({ queryKey: [ASSET_STATE_COUNTS_QUERY_KEY], exact: false });
      queryClient.resetQueries({ queryKey: [HAS_SUBFOLDER_DESCRIPTIONS_QUERY_KEY], exact: false });
      queryClient.resetQueries({ queryKey: [FOLDER_COLUMN_VISIBILITY_QUERY_KEY], exact: false });
    },
    onError: (error: AxiosError<{ message: string }>) => {
      onError?.(error.response?.data.message ?? 'Failed to delete folder');
    },
  });

  return {
    mutate,
    isPending,
    error,
  };
};
